import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Globe } from 'lucide-react';

const sections = [
  { id: 'eligibility', title: '1. Eligibility and Account Registration' },
  { id: 'nature', title: '2. Nature of the Platform' },
  { id: 'currencies', title: '3. Virtual Currencies and Rewards' },
  { id: 'tasks', title: '4. Task-Based Reward System' },
  { id: 'ads', title: '5. Advertising and Non-Rewarded Ads' },
  { id: 'programs', title: '6. Daily Programs and Promotional Reward Pools' },
  { id: 'no-gambling', title: '7. No Gambling, Lottery, or Betting' },
  { id: 'no-earnings', title: '8. No Guaranteed Earnings' },
  { id: 'offerwall', title: '9. Offerwall and Third-Party Services' },
  { id: 'fraud', title: '10. Anti-Fraud and Invalid Activity' },
  { id: 'ad-integrity', title: '11. Advertising Integrity' },
  { id: 'withdrawals', title: '12. Withdrawals and Redemption' },
  { id: 'referral', title: '13. Referral Program' },
  { id: 'messaging', title: '14. Messaging and Community Features' },
  { id: 'ip', title: '15. Intellectual Property' },
  { id: 'modifications', title: '16. Platform Modifications' },
  { id: 'termination', title: '17. Account Termination' },
  { id: 'liability', title: '18. Limitation of Liability' },
  { id: 'indemnification', title: '19. Indemnification' },
  { id: 'privacy', title: '20. Privacy Policy' },
  { id: 'law', title: '21. Governing Law and Jurisdiction' },
  { id: 'severability', title: '22. Severability' },
  { id: 'contact', title: '23. Contact Information' },
  { id: 'acceptance', title: '24. Acceptance of Terms' },
];

const H2 = ({ id, children }) => (
  <h2
    id={id}
    className="mt-14 scroll-mt-32 text-2xl md:text-[28px] font-semibold tracking-tightest text-ink"
  >
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="mt-6 text-lg font-semibold text-ink">{children}</h3>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] md:text-base leading-[1.8] text-ink/75">
    {children}
  </p>
);

const UL = ({ children }) => (
  <ul className="mt-4 space-y-2.5 text-[15px] md:text-base leading-[1.8] text-ink/75 pl-1">
    {children}
  </ul>
);

const LI = ({ children }) => (
  <li className="relative pl-6 before:content-[''] before:absolute before:left-0 before:top-[0.7em] before:w-2 before:h-2 before:bg-brand before:rounded-sm">
    {children}
  </li>
);

export default function Terms() {
  return (
    <div
      data-testid="terms-page"
      className="min-h-screen bg-white px-5 md:px-10 pt-28 md:pt-32 pb-20"
    >
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 text-ink/60 hover:text-brand transition-colors text-sm"
        >
          <ArrowLeft size={16} /> Back to home
        </Link>

        <span className="mt-8 block font-pixel text-[11px] tracking-[0.25em] uppercase text-brand">
          Legal
        </span>
        <h1 className="mt-3 text-4xl md:text-6xl font-semibold tracking-tightest leading-[1.05]">
          Terms &amp; Conditions
        </h1>
        <p
          data-testid="terms-last-updated"
          className="mt-5 font-pixel text-[11px] tracking-[0.2em] uppercase text-ink/50"
        >
          Last updated · April 21, 2026
        </p>

        <div className="mt-8 rounded-3xl bg-surface-muted border border-black/5 p-7 md:p-8">
          <p className="text-[15px] md:text-base leading-[1.8] text-ink/75">
            These Terms &amp; Conditions (&quot;Terms&quot;) govern your access
            to and use of the <strong>Coinsflu</strong> mobile application
            (&quot;App&quot;, &quot;Platform&quot;, &quot;Service&quot;)
            operated by <strong>Kings Wake Digital Private Limited</strong>{' '}
            (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;,
            &quot;our&quot;). By downloading, installing, registering for, or
            using Coinsflu, you acknowledge that you have read, understood, and
            agreed to be legally bound by these Terms. If you do not agree, you
            must not use the App.
          </p>
        </div>

        {/* Table of contents */}
        <nav
          aria-label="Table of contents"
          data-testid="terms-toc"
          className="mt-10 rounded-3xl border border-black/5 p-6 md:p-7"
        >
          <div className="font-pixel text-[10px] tracking-[0.25em] uppercase text-ink/40">
            Contents
          </div>
          <ol className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[13.5px] text-ink/70">
            {sections.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="hover:text-brand transition-colors"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="mt-4">
          <H2 id="eligibility">1. Eligibility and Account Registration</H2>
          <UL>
            <LI>
              You must be at least <strong>18 years old</strong> (or the minimum
              age required by law in your jurisdiction) to use the App. The App
              is not directed to children under 13 years of age and is not
              intended for use by anyone under 18.
            </LI>
            <LI>
              Users must provide accurate, complete, and truthful information
              during registration, including a valid mobile number for OTP
              verification.
            </LI>
            <LI>
              Each user is allowed to maintain <strong>only one account</strong>{' '}
              on the platform. Operating multiple accounts, creating duplicate
              accounts, sharing accounts, or attempting to bypass system
              controls is strictly prohibited.
            </LI>
            <LI>
              Users are responsible for maintaining the confidentiality of
              their login credentials, OTP, and PIN, and for all activities
              occurring under their account.
            </LI>
            <LI>
              You agree to comply with all applicable laws and these Terms when
              using the App.
            </LI>
            <LI>
              We reserve the right to refuse registration, suspend, or
              terminate any account at our sole discretion.
            </LI>
          </UL>

          <H2 id="nature">2. Nature of the Platform</H2>
          <UL>
            <LI>
              Coinsflu is an{' '}
              <strong>
                entertainment-based social engagement and loyalty platform
              </strong>{' '}
              where users may participate in optional activities and
              promotional tasks.
            </LI>
            <LI>
              The App{' '}
              <strong>
                does not provide employment, investment services, financial
                returns, or guaranteed income
              </strong>
              .
            </LI>
            <LI>
              All rewards, promotions, and in-app benefits are engagement-based
              and may vary depending on user activity, partner availability,
              and platform conditions.
            </LI>
            <LI>
              Coinsflu fully complies with all applicable Indian laws,
              including the{' '}
              <strong>
                Promotion and Regulation of Online Gaming Act, 2025
              </strong>
              , and does <strong>not</strong> offer, operate, facilitate, or
              constitute an &quot;online money game&quot; or &quot;online money
              gaming service&quot; as defined under that Act.
            </LI>
          </UL>

          <H2 id="currencies">3. Virtual Currencies and Rewards</H2>
          <P>Coinsflu operates two distinct types of virtual currency:</P>

          <H3>A. VIP Coins (Non-Redeemable)</H3>
          <UL>
            <LI>
              Earned by completing in-app engagement tasks such as watching
              ads, completing surveys, or installing partner applications.
            </LI>
            <LI>
              Used solely for unlocking in-app benefits, profile enhancements,
              and VIP status.
            </LI>
            <LI>
              <strong>Cannot</strong> be withdrawn, transferred, exchanged for
              cash, or redeemed for real-world value.
            </LI>
            <LI>Have no monetary value.</LI>
          </UL>

          <H3>B. W Coins (Redeemable Promotional Currency)</H3>
          <UL>
            <LI>
              Earned by participating in and winning daily program events on
              the platform.
            </LI>
            <LI>
              May be redeemed for real-world value (such as UPI transfers) at
              the published conversion rate of approximately{' '}
              <strong>1,079 W Coins = 1 USD</strong>, subject to change at the
              Company&apos;s sole discretion.
            </LI>
            <LI>
              Subject to minimum withdrawal thresholds, eligibility, KYC
              verification, and the App&apos;s promotional terms.
            </LI>
          </UL>

          <H3>General Conditions Applicable to All Virtual Currencies</H3>
          <UL>
            <LI>
              Virtual coins are digital promotional units used within the
              platform.
            </LI>
            <LI>They do not represent legal tender or financial assets.</LI>
            <LI>They do not have a fixed or guaranteed monetary value.</LI>
            <LI>
              Their value or availability may fluctuate depending on
              advertising revenue, platform economics, partner verification
              outcomes, and promotional programs.
            </LI>
            <LI>
              They are not &quot;stakes&quot; or &quot;other stakes&quot;
              (including credits, coins, or tokens equivalent or convertible to
              money) as defined under applicable Indian online gaming laws, as
              they involve no user payment, deposit, stake, wager, or risk and
              are not guaranteed to be convertible to money at any fixed rate.
            </LI>
            <LI>
              The platform reserves the right to modify the value, conversion
              rate, redemption conditions, or availability of any virtual
              currency at any time.
            </LI>
          </UL>

          <H2 id="tasks">4. Task-Based Reward System</H2>
          <UL>
            <LI>
              Users may earn rewards by completing promotional tasks available
              within the App.
            </LI>
            <LI>
              Tasks may include actions such as:
              <UL>
                <LI>Completing offers from third-party partners</LI>
                <LI>App installations</LI>
                <LI>Website visits</LI>
                <LI>Surveys or promotional engagements</LI>
                <LI>Watching rewarded video advertisements</LI>
              </UL>
            </LI>
            <LI>
              Rewards are credited <strong>only after successful verification</strong>{' '}
              of task completion by the respective partner or platform system.
            </LI>
            <LI>
              Coinsflu does not guarantee approval of any task, as verification
              may depend on third-party partners and their fraud-detection
              systems.
            </LI>
            <LI>
              Verification times may vary from a few seconds to several days,
              depending on the partner.
            </LI>
          </UL>

          <H2 id="ads">5. Advertising and Non-Rewarded Ads</H2>
          <UL>
            <LI>
              The App may display advertisements including but not limited to:
              <UL>
                <LI>App Open Ads</LI>
                <LI>Banner Ads</LI>
                <LI>Interstitial Ads</LI>
                <LI>Native Ads</LI>
              </UL>
            </LI>
            <LI>
              Viewing, clicking, or interacting with these advertisements{' '}
              <strong>does not</strong> generate withdrawable rewards.
            </LI>
            <LI>
              Only approved tasks completed through designated promotional
              systems and offerwalls may generate eligible rewards.
            </LI>
          </UL>

          <H2 id="programs">6. Daily Programs and Promotional Reward Pools</H2>
          <UL>
            <LI>
              The platform organizes daily promotional engagement programs
              (&quot;Programs&quot;, sometimes referred to as Gift Pools or
              Today&apos;s Gifts).
            </LI>
            <LI>
              These programs operate under the following conditions:
              <UL>
                <LI>
                  Users do <strong>not</strong> contribute real money or stake
                  funds to participate.
                </LI>
                <LI>
                  Participation is free and occurs through engagement
                  activities on the platform (completing tasks earns program
                  entries).
                </LI>
                <LI>
                  The promotional reward pools are funded entirely by the
                  platform.
                </LI>
              </UL>
            </LI>
            <LI>
              Users receive their standard task rewards (VIP Coins) immediately
              after completing eligible tasks; program participation is an
              additional, optional benefit.
            </LI>
            <LI>
              Program winners are determined based on engagement metrics such
              as streak, participation, and platform-defined criteria — not by
              chance, lottery, or wagering.
            </LI>
            <LI>
              Participation in promotional pools does not require users to
              risk, deposit, or wager money.
            </LI>
            <LI>
              Program rules, schedules, winner counts, and reward amounts may
              change at any time without prior notice.
            </LI>
          </UL>

          <H2 id="no-gambling">7. No Gambling, Lottery, or Betting</H2>
          <UL>
            <LI>
              Coinsflu is <strong>not</strong> a gambling, lottery, betting, or
              wagering platform.
            </LI>
            <LI>
              The App does not allow users to:
              <UL>
                <LI>Place bets</LI>
                <LI>Deposit money for chance-based winnings</LI>
                <LI>Stake money to compete for rewards</LI>
                <LI>Purchase entries, tickets, or chances</LI>
              </UL>
            </LI>
            <LI>
              All rewards are based on engagement, participation, and
              completion of tasks rather than luck or wagering.
            </LI>
            <LI>
              The platform does not involve any element of chance-based
              winnings, user staking, wagering, or expectation of monetary
              enrichment through risk, and thus does not fall within the
              definition of prohibited &quot;online money games&quot; under the
              Promotion and Regulation of Online Gaming Act, 2025.
            </LI>
          </UL>

          <H2 id="no-earnings">8. No Guaranteed Earnings</H2>
          <UL>
            <LI>
              The platform does not guarantee any specific level of earnings.
            </LI>
            <LI>
              User rewards may vary based on factors including:
              <UL>
                <LI>Availability of promotional tasks</LI>
                <LI>Advertising demand and partner inventory</LI>
                <LI>Partner verification results</LI>
                <LI>User participation, consistency, and streak</LI>
                <LI>Geographic location of the user</LI>
              </UL>
            </LI>
            <LI>
              Earnings may increase, decrease, or become unavailable at any
              time.
            </LI>
            <LI>Past performance is not indicative of future rewards.</LI>
          </UL>

          <H2 id="offerwall">9. Offerwall and Third-Party Services</H2>
          <UL>
            <LI>
              Coinsflu may integrate with third-party offerwall and advertising
              partners that provide promotional tasks, including but not
              limited to: Google AdMob, AdGem, ayeT-Studios, BitLabs, Pollfish,
              CPX Research, TheoremReach, Lootably, AdGate Media, OfferToro,
              and Adscend Media.
            </LI>
            <LI>
              When completing such tasks:
              <UL>
                <LI>
                  Task requirements, payouts, and approval criteria are defined
                  by the partner.
                </LI>
                <LI>
                  Reward approvals are determined by the partner&apos;s
                  verification systems via server-to-server (S2S) postback.
                </LI>
                <LI>Task availability may change without notice.</LI>
                <LI>
                  Some tasks may require providing information directly to the
                  partner, governed by that partner&apos;s privacy policy.
                </LI>
              </UL>
            </LI>
            <LI>
              Coinsflu is <strong>not responsible</strong> for partner
              decisions regarding approval, rejection, delay, or reversal of
              rewards.
            </LI>
            <LI>
              Users agree to comply with all applicable advertising partner
              policies when engaging with tasks or offers. Failure to comply
              with partner policies may result in forfeiture of rewards or
              account suspension.
            </LI>
          </UL>

          <H2 id="fraud">10. Anti-Fraud and Invalid Activity</H2>
          <P>
            To maintain platform integrity and comply with advertising partner
            policies, the following activities are{' '}
            <strong>strictly prohibited</strong>:
          </P>
          <UL>
            <LI>Generating artificial traffic</LI>
            <LI>Using bots, scripts, or automated tools</LI>
            <LI>Operating emulators or virtual devices</LI>
            <LI>
              Using VPNs, proxy services, or location spoofing tools to
              manipulate location or identity
            </LI>
            <LI>Creating multiple accounts or sharing devices across accounts</LI>
            <LI>
              Attempting to exploit, reverse-engineer, or manipulate the reward
              system
            </LI>
            <LI>Submitting false UPI details or payment information</LI>
            <LI>
              Colluding with other users to manipulate referral or engagement
              systems
            </LI>
          </UL>
          <P>
            Accounts involved in such activities may be{' '}
            <strong>permanently suspended or terminated without notice</strong>,
            and all pending or accumulated rewards (including W Coins) may be
            forfeited. The Company&apos;s determination of fraudulent activity,
            made in good faith, shall be final and binding.
          </P>

          <H2 id="ad-integrity">11. Advertising Integrity</H2>
          <P>
            Users must not engage in activities that generate invalid
            advertising traffic, including:
          </P>
          <UL>
            <LI>Repeated or automated ad clicking</LI>
            <LI>Encouraging others to click ads (incentivized clicks)</LI>
            <LI>Manipulating advertisement interactions</LI>
            <LI>
              Using ad-blocking tools to bypass or alter ad delivery while
              claiming rewards
            </LI>
          </UL>
          <P>
            Violations may lead to immediate account suspension to protect
            advertising partner compliance and platform integrity.
          </P>

          <H2 id="withdrawals">12. Withdrawals and Redemption</H2>
          <UL>
            <LI>
              Where available, the platform may provide reward redemption
              options for <strong>W Coins only</strong>, subject to certain
              conditions including:
              <UL>
                <LI>Minimum withdrawal threshold (as published in-app)</LI>
                <LI>Identity and account verification (KYC where required)</LI>
                <LI>UPI ID verification</LI>
                <LI>Anti-fraud review of the user&apos;s activity history</LI>
              </UL>
            </LI>
            <LI>
              Withdrawal methods, limits, conversion rates, and processing
              times may change without prior notice.
            </LI>
            <LI>
              Standard processing time is up to{' '}
              <strong>7-14 working days</strong> from approval.
            </LI>
            <LI>
              The platform reserves the right to review, hold, or reject
              transactions to prevent fraud, abuse, or violations of these
              Terms or partner policies.
            </LI>
            <LI>
              Withdrawals successfully processed and disbursed are final.
              Failed or reversed UPI transactions will be re-credited to the
              user&apos;s W Coin balance.
            </LI>
            <LI>
              Rewards are promotional in nature; no cash equivalents or
              guaranteed monetary payouts are provided, and all redemptions
              (if any) are subject to strict verification to ensure compliance
              with applicable laws and partner policies.
            </LI>
            <LI>
              Users are solely responsible for any tax obligations arising from
              rewards received.
            </LI>
          </UL>

          <H2 id="referral">13. Referral Program</H2>
          <UL>
            <LI>
              Users may invite others to join Coinsflu using a unique referral
              link or code.
            </LI>
            <LI>
              Referral rewards are subject to the referred user genuinely
              engaging with the platform.
            </LI>
            <LI>
              Self-referrals, fake referrals, and use of incentivized signup
              services are prohibited and may result in forfeiture of all
              referral rewards and account suspension.
            </LI>
            <LI>Referral reward amounts and conditions may change at any time.</LI>
          </UL>

          <H2 id="messaging">14. Messaging and Community Features</H2>
          <UL>
            <LI>
              Users may communicate with other users through messaging or
              community features within the App.
            </LI>
            <LI>
              Users must not share:
              <UL>
                <LI>Illegal, obscene, or harmful content</LI>
                <LI>Abusive, threatening, or harassing material</LI>
                <LI>Misleading, fraudulent, or deceptive information</LI>
                <LI>Spam, unsolicited promotions, or external links</LI>
                <LI>Content that infringes intellectual property rights</LI>
                <LI>
                  Personally identifiable information of others without consent
                </LI>
              </UL>
            </LI>
            <LI>
              The platform reserves the right to moderate, remove, or block
              content and users who violate these Terms.
            </LI>
          </UL>

          <H2 id="ip">15. Intellectual Property</H2>
          <UL>
            <LI>
              All content, trademarks, logos, designs, software, and materials
              within the App are the property of{' '}
              <strong>Kings Wake Digital Private Limited</strong> or its
              licensors.
            </LI>
            <LI>
              Users are granted a limited, non-exclusive, non-transferable
              license to use the App for personal, non-commercial purposes.
            </LI>
            <LI>
              Users must not copy, modify, distribute, sell, reverse-engineer,
              or create derivative works of the App or its content.
            </LI>
          </UL>

          <H2 id="modifications">16. Platform Modifications</H2>
          <P>
            Coinsflu reserves the right to modify, suspend, or discontinue any
            part of the platform at any time, including:
          </P>
          <UL>
            <LI>Reward structures and conversion rates</LI>
            <LI>Promotional programs and daily events</LI>
            <LI>Virtual coin systems</LI>
            <LI>Features, services, or third-party integrations</LI>
            <LI>These Terms themselves</LI>
          </UL>
          <P>
            Such changes may occur without prior notice. Continued use of the
            App after changes constitutes acceptance of the modified Terms.
          </P>

          <H2 id="termination">17. Account Termination</H2>
          <UL>
            <LI>
              Users may delete their account at any time through the in-app
              settings or by contacting support.
            </LI>
            <LI>
              The Company may suspend or terminate any account, with or without
              notice, for:
              <UL>
                <LI>Violation of these Terms</LI>
                <LI>Fraudulent or suspicious activity</LI>
                <LI>Violation of partner policies</LI>
                <LI>Legal or regulatory requirements</LI>
                <LI>Extended inactivity (24+ months)</LI>
              </UL>
            </LI>
            <LI>
              Upon termination for cause, all pending and accumulated rewards
              may be forfeited without compensation.
            </LI>
          </UL>

          <H2 id="liability">18. Limitation of Liability</H2>
          <P>
            Coinsflu strives to provide a reliable platform but does not
            guarantee uninterrupted or error-free operation. To the maximum
            extent permitted by law, the Company shall not be liable for:
          </P>
          <UL>
            <LI>Loss of rewards due to task rejection by partners</LI>
            <LI>Technical interruptions, downtime, or data loss</LI>
            <LI>Partner service failures or policy changes</LI>
            <LI>Temporary or permanent system outages</LI>
            <LI>Indirect, incidental, consequential, or punitive damages</LI>
            <LI>
              Any amount exceeding the value of rewards earned by the user in
              the 30 days preceding the event giving rise to the claim
            </LI>
          </UL>
          <P>
            The App is provided on an <strong>&quot;as is&quot;</strong> and{' '}
            <strong>&quot;as available&quot;</strong> basis without warranties
            of any kind, express or implied.
          </P>

          <H2 id="indemnification">19. Indemnification</H2>
          <P>
            You agree to indemnify, defend, and hold harmless{' '}
            <strong>Kings Wake Digital Private Limited</strong>, its officers,
            employees, and partners from any claims, losses, liabilities,
            damages, costs, or expenses (including reasonable legal fees)
            arising from:
          </P>
          <UL>
            <LI>Your use or misuse of the App</LI>
            <LI>Your violation of these Terms</LI>
            <LI>Your violation of any law or third-party rights</LI>
            <LI>Your violation of any advertising partner&apos;s policies</LI>
          </UL>

          <H2 id="privacy">20. Privacy Policy</H2>
          <UL>
            <LI>
              User data is handled in accordance with our{' '}
              <Link
                to="/privacy"
                data-testid="terms-privacy-link"
                className="text-brand underline underline-offset-2"
              >
                Privacy Policy
              </Link>
              , available within the App and at:{' '}
              <a
                href="https://coinsflu.kingswake.co.in"
                className="text-brand underline underline-offset-2 break-all"
                target="_blank"
                rel="noreferrer"
              >
                https://coinsflu.kingswake.co.in
              </a>
              .
            </LI>
            <LI>
              By using the App, you consent to the collection and use of your
              information as outlined in the Privacy Policy.
            </LI>
          </UL>

          <H2 id="law">21. Governing Law and Jurisdiction</H2>
          <UL>
            <LI>
              These Terms shall be governed by and interpreted in accordance
              with the <strong>laws of India</strong>.
            </LI>
            <LI>
              Any disputes arising from the use of the platform shall be
              subject to the{' '}
              <strong>
                exclusive jurisdiction of the courts located in Ludhiana,
                Punjab, India
              </strong>
              .
            </LI>
          </UL>

          <H2 id="severability">22. Severability</H2>
          <P>
            If any provision of these Terms is found to be invalid, illegal, or
            unenforceable by a court of competent jurisdiction, the remaining
            provisions shall continue in full force and effect.
          </P>

          <H2 id="contact">23. Contact Information</H2>
          <P>
            For any questions, concerns, or grievances regarding these Terms,
            please contact:
          </P>
          <div className="mt-5 rounded-3xl bg-surface-muted border border-black/5 p-6 md:p-7">
            <div className="font-pixel text-[10px] tracking-[0.25em] uppercase text-ink/40">
              Kings Wake Digital Private Limited
            </div>
            <div className="mt-4 space-y-3 text-[15px] text-ink/80">
              <a
                href="mailto:team@kingswake.co.in"
                data-testid="terms-email-link"
                className="inline-flex items-center gap-2 hover:text-brand transition-colors"
              >
                <Mail size={16} className="text-brand" />
                team@kingswake.co.in
              </a>
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-brand" />
                <a
                  href="https://kingswake.co.in"
                  className="hover:text-brand transition-colors"
                  target="_blank"
                  rel="noreferrer"
                >
                  https://kingswake.co.in
                </a>
              </div>
              <div className="text-ink/70">
                Grievance Officer: Available at the same contact (in compliance
                with the IT Act and DPDP Act, 2023)
              </div>
            </div>
          </div>

          <H2 id="acceptance">24. Acceptance of Terms</H2>
          <P>
            By downloading, installing, registering for, or using the Coinsflu
            App, you acknowledge that you have{' '}
            <strong>read, understood, and agreed</strong> to these Terms &amp;
            Conditions, along with the Privacy Policy. If you do not agree with
            any part of these Terms, you must immediately discontinue use of
            the App.
          </P>
        </article>

        <div className="mt-16 flex items-center justify-between border-t border-black/5 pt-6">
          <Link
            to="/"
            data-testid="terms-back-link-bottom"
            className="inline-flex items-center gap-2 text-ink/60 hover:text-brand transition-colors text-sm"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>
          <Link
            to="/privacy"
            data-testid="terms-to-privacy-link"
            className="text-sm text-brand hover:text-brand-hover transition-colors"
          >
            Read Privacy Policy →
          </Link>
        </div>
      </div>
    </div>
  );
}
